// game/client/src/hud/expertise-arc.ts
//
// The always-open expertise arc (B5 progression). v3 (2026-06-18): a COMPACT
// horizontal ROW of 5 tree badges centered just above the bottom main-plate.
// v2 pinned this to the LEFT edge, but that overlapped the 48px edge-pan zone
// (left 14–48px) — hovering/clicking a badge fought the camera scroll. Moving
// it above the plate (a free band between the plate top ~182px and the build
// menu at 320px) clears the edge-pan zone entirely. Each badge shows:
//   - tree icon + tier digit
//   - a 5-pip row (one pip per tier; lit once tier >= its number)
//   - click-to-spend: clicking a non-maxed badge sends `spendExpertisePoint`.
//   - spendable affordance: gold-rim pulse when a banked point can go here.
// A banked "+N" chip sits at the top of the strip and pulses while points are
// unspent. The hover tooltip explains your CURRENT tier and, crucially, what the
// NEXT tier gives (the per-tier copy lives in expertise-data.ts; the full trees
// live in the Help panel).
//
// Server-authoritative + gated: the UI only sends; the schema update reflects
// back via the per-frame refresh(cube). No optimistic mutation.

import type { Cube } from "@tribez/shared";
import { TREE_CAP, TREE_DEFS, type TreeDef, type TreeField, type TreeKey } from "./expertise-data";

/** Perk-tier thresholds — one perk per tier (1..5). A pip is "earned" when the
 *  tree's tier >= its threshold; the 5 pips ARE the 5 tiers. */
const PERK_PIPS = [1, 2, 3, 4, 5] as const;

/** Inline-SVG glyph per tree (presentational; data lives in expertise-data.ts). */
const TREE_GLYPHS: Record<TreeKey, string> = {
  Forestry: treeGlyph(),
  Mining: pickGlyph(),
  Cooking: potGlyph(),
  Foraging: leafGlyph(),
  Artisanship: hammerGlyph(),
};

const STYLE_ID = "exp-arc-style";
const CSS = `
.exp-arc {
  position: fixed; left: 50%; bottom: 188px; transform: translateX(-50%);
  z-index: 20; pointer-events: none;
  display: flex; flex-direction: row; align-items: center; gap: 8px;
  padding: 6px 10px;
  background: linear-gradient(180deg, rgba(20, 18, 14, 0.88), rgba(10, 8, 6, 0.88));
  border: 1px solid var(--mp-rim, rgba(200, 165, 90, 0.32));
  border-radius: 9px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.55), inset 0 0 0 1px rgba(247, 195, 96, 0.06);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  user-select: none;
}
/* Banked-point "+N" chip — pulses gold when points are unspent. */
.exp-arc__banked {
  display: none;
  min-width: 20px; padding: 1px 6px; border-radius: 9px;
  background: #f7c360; color: #1a1208;
  font: 700 12px/1.5 system-ui, sans-serif; text-align: center;
  box-shadow: 0 0 8px rgba(247, 195, 96, 0.7);
  animation: exp-pulse 1.1s ease-in-out infinite;
}
.exp-arc__banked.has-points { display: block; }
@keyframes exp-pulse {
  0%, 100% { transform: scale(1);    box-shadow: 0 0 6px rgba(247, 195, 96, 0.55); }
  50%      { transform: scale(1.12); box-shadow: 0 0 13px rgba(247, 195, 96, 0.95); }
}

.exp-badge {
  pointer-events: auto; position: relative;
  width: 38px; height: 38px; padding: 0;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 3px;
  border-radius: 7px; border: 1px solid rgba(200, 165, 90, 0.22);
  background: rgba(0, 0, 0, 0.30);
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.exp-badge__icon { width: 19px; height: 19px; color: #d8c79a; }
.exp-badge__icon svg { width: 100%; height: 100%; display: block; }
.exp-badge__tier {
  position: absolute; top: 1px; right: 4px;
  font: 700 9px/1 system-ui, sans-serif; color: #c8a55a;
  font-variant-numeric: tabular-nums; text-shadow: 0 1px 2px #000;
}
.exp-badge__pips { display: flex; gap: 2px; }
.exp-pip {
  width: 4px; height: 4px; transform: rotate(45deg);
  background: #1a1612; border: 1px solid rgba(160, 130, 70, 0.55);
  border-radius: 1px;
  transition: background 0.2s, box-shadow 0.2s, border-color 0.2s;
}
.exp-pip.earned {
  background: #fff0c0; border-color: #fff;
  box-shadow: 0 0 4px rgba(255, 240, 192, 0.95);
}
/* Spendable: a banked point AND this tree isn't capped → gold-rim pulse. */
.exp-badge.spendable { cursor: pointer; border-color: rgba(247, 200, 110, 0.7); }
.exp-badge.spendable:hover { background: rgba(247, 195, 96, 0.12); border-color: rgba(247, 210, 130, 0.95); }
.exp-badge.spendable { box-shadow: 0 0 8px rgba(247, 195, 96, 0.35); }
.exp-badge.maxed { opacity: 0.6; }
.exp-badge.maxed .exp-badge__tier { color: #f7c360; }
.exp-badge.locked { opacity: 0.9; }

/* Tooltip — themed like the v10 chrome. */
.exp-tip {
  position: fixed; z-index: 60; pointer-events: none; display: none;
  max-width: 236px; padding: 9px 11px; border-radius: 6px;
  background: rgba(12, 16, 22, 0.96);
  border: 1px solid rgba(200, 165, 90, 0.5);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.7);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}
.exp-tip__name {
  font: 700 12px/1.2 system-ui, sans-serif; color: #f7c360;
  letter-spacing: 0.03em; margin-bottom: 5px;
}
.exp-tip__cur {
  font: 600 11px/1.35 system-ui, sans-serif; color: #9aa4ad; margin-bottom: 6px;
}
.exp-tip__cur b { color: #cdd6df; font-weight: 700; }
.exp-tip__next {
  border-left: 2px solid rgba(247, 195, 96, 0.6); padding-left: 7px;
}
.exp-tip__nextlabel {
  font: 700 11px/1.3 system-ui, sans-serif; color: #f7c360; letter-spacing: 0.02em;
}
.exp-tip__eff { font: 400 12px/1.4 system-ui, sans-serif; color: #d8e0ea; margin-top: 2px; }
.exp-tip__hint { margin-top: 7px; font: 600 11px/1.2 system-ui, sans-serif; }
.exp-tip__hint.spend { color: #9ee27a; }
.exp-tip__hint.none  { color: #8a8578; }
.exp-tip__hint.max   { color: #f7c360; }
`;

export interface ExpertiseArc {
  /** Mirror schema state into the arc. Call each frame from main.ts. */
  refresh(cube: Cube): void;
  /** Cleanup (remove DOM + injected style). */
  dispose(): void;
}

type SendFn = (type: string, payload: unknown) => void;

interface BadgeRefs {
  root: HTMLButtonElement;
  tier: HTMLDivElement;
  pips: HTMLDivElement[];
  lastInvested: number;
  lastClass: string;
}

/**
 * @param send  Wire-send fn (forwards to room.send). The factory takes only
 *   the send fn — it never holds a Room reference (matches main-plate).
 */
export function createExpertiseArc(send: SendFn): ExpertiseArc {
  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  const root = document.createElement("div");
  root.className = "exp-arc";

  const bankedBadge = document.createElement("span");
  bankedBadge.className = "exp-arc__banked";
  root.appendChild(bankedBadge);

  // Shared tooltip (one per arc — created here, positioned on hover).
  const tip = document.createElement("div");
  tip.className = "exp-tip";
  document.body.appendChild(tip);

  const badges = new Map<TreeField, BadgeRefs>();

  for (const tree of TREE_DEFS) {
    const btn = document.createElement("button");
    btn.className = "exp-badge locked";
    btn.type = "button";
    btn.dataset.tree = tree.key;

    const pipsHtml = PERK_PIPS.map(() => `<span class="exp-pip"></span>`).join("");
    btn.innerHTML = `
      <div class="exp-badge__tier">0</div>
      <span class="exp-badge__icon">${TREE_GLYPHS[tree.key]}</span>
      <div class="exp-badge__pips">${pipsHtml}</div>
    `;

    const refs: BadgeRefs = {
      root: btn,
      tier: btn.querySelector<HTMLDivElement>(".exp-badge__tier")!,
      pips: Array.from(btn.querySelectorAll<HTMLDivElement>(".exp-pip")),
      lastInvested: -1,
      lastClass: "",
    };
    badges.set(tree.field, refs);

    // Click → spend. The handler reads the live spendable state off the button's
    // class (set in refresh); the server re-gates regardless.
    btn.addEventListener("click", () => {
      if (!btn.classList.contains("spendable")) return;
      send("spendExpertisePoint", { tree: tree.key });
    });

    btn.addEventListener("mouseenter", (ev) => showTip(tree, btn, ev as MouseEvent));
    btn.addEventListener("mousemove", (ev) => positionTip(ev as MouseEvent));
    btn.addEventListener("mouseleave", () => { tip.style.display = "none"; });

    root.appendChild(btn);
  }

  document.body.appendChild(root);

  function showTip(tree: TreeDef, btn: HTMLButtonElement, ev: MouseEvent): void {
    const invested = Number(btn.dataset.invested ?? "0");
    const banked = Number(root.dataset.banked ?? "0");
    const maxed = invested >= TREE_CAP;

    // Current-tier context line (omitted at tier 0).
    const cur = invested > 0
      ? `<div class="exp-tip__cur">Now · Tier ${invested}: <b>${tree.tiers[invested - 1]!.name}</b></div>`
      : `<div class="exp-tip__cur">${tree.summary}</div>`;

    // The headline: what the NEXT tier grants (or the capstone, if maxed).
    let next: string;
    let hint: string;
    if (maxed) {
      const cap = tree.tiers[TREE_CAP - 1]!;
      next = `<div class="exp-tip__next"><div class="exp-tip__nextlabel">Maxed · Tier ${TREE_CAP}: ${cap.name}</div><div class="exp-tip__eff">${cap.effect}</div></div>`;
      hint = `<div class="exp-tip__hint max">Fully invested (${TREE_CAP}/${TREE_CAP})</div>`;
    } else {
      const nt = tree.tiers[invested]!; // tier (invested+1), 0-indexed
      next = `<div class="exp-tip__next"><div class="exp-tip__nextlabel">Next · Tier ${invested + 1}: ${nt.name}</div><div class="exp-tip__eff">${nt.effect}</div></div>`;
      hint = banked > 0
        ? `<div class="exp-tip__hint spend">Click to spend 1 point</div>`
        : `<div class="exp-tip__hint none">Level up to earn a point</div>`;
    }

    tip.innerHTML = `
      <div class="exp-tip__name">${tree.key} · ${invested}/${TREE_CAP}</div>
      ${cur}
      ${next}
      ${hint}
    `;
    tip.style.display = "block";
    positionTip(ev);
  }

  function positionTip(ev: MouseEvent): void {
    const offset = 14;
    const r = tip.getBoundingClientRect();
    let x = ev.clientX + offset;
    let y = ev.clientY + offset;
    if (x + r.width > window.innerWidth) x = ev.clientX - r.width - offset;
    if (y + r.height > window.innerHeight) y = ev.clientY - r.height - offset;
    tip.style.left = `${Math.max(4, x)}px`;
    tip.style.top = `${Math.max(4, y)}px`;
  }

  function refresh(cube: Cube): void {
    // `expertise` may be undefined before the first delta carrying the sub-schema
    // arrives (same race main-plate / ability-bar guard). Treat as all-zero.
    const exp = cube.expertise;
    const banked = exp?.bankedPoints ?? 0;
    root.dataset.banked = String(banked);

    if (banked > 0) {
      bankedBadge.textContent = `+${banked}`;
      bankedBadge.classList.add("has-points");
    } else {
      bankedBadge.classList.remove("has-points");
    }

    for (const tree of TREE_DEFS) {
      const refs = badges.get(tree.field)!;
      const invested = (exp?.[tree.field] ?? 0) | 0;
      refs.root.dataset.invested = String(invested);

      if (invested !== refs.lastInvested) {
        refs.lastInvested = invested;
        refs.tier.textContent = String(invested);
        for (let i = 0; i < refs.pips.length; i++) {
          refs.pips[i]!.classList.toggle("earned", invested >= PERK_PIPS[i]!);
        }
      }

      const maxed = invested >= TREE_CAP;
      const cls = maxed ? "maxed" : banked > 0 ? "spendable" : "locked";
      if (cls !== refs.lastClass) {
        refs.lastClass = cls;
        refs.root.classList.remove("spendable", "maxed", "locked");
        refs.root.classList.add(cls);
        refs.root.disabled = cls !== "spendable";
      }
    }
  }

  function dispose(): void {
    root.remove();
    tip.remove();
  }

  return { refresh, dispose };
}

// ── Inline-SVG glyphs (16×16 viewBox, currentColor) ────────────────────────
// Simple silhouettes that read at small size against the dark badge.

function treeGlyph(): string {
  return `<svg viewBox="0 0 16 16"><path d="M8 1 L11.5 6 H9.5 L12.5 10 H3.5 L6.5 6 H4.5 Z" fill="currentColor"/><rect x="7" y="10" width="2" height="4" fill="currentColor"/></svg>`;
}
function pickGlyph(): string {
  return `<svg viewBox="0 0 16 16"><path d="M2 4 Q8 1 14 4 Q8 3.5 2 4 Z" fill="currentColor"/><rect x="7.2" y="3.5" width="1.6" height="11" rx="0.6" fill="currentColor" transform="rotate(-12 8 8)"/></svg>`;
}
function potGlyph(): string {
  return `<svg viewBox="0 0 16 16"><path d="M3 6 H13 L12 13 H4 Z" fill="currentColor"/><rect x="2" y="5" width="12" height="1.6" rx="0.8" fill="currentColor"/><path d="M6 4 Q8 1.5 10 4" stroke="currentColor" stroke-width="1" fill="none"/></svg>`;
}
function leafGlyph(): string {
  return `<svg viewBox="0 0 16 16"><path d="M13 2 Q4 3 3 13 Q12 12 13 2 Z" fill="currentColor"/><path d="M3 13 Q8 8 13 2" stroke="#1a1612" stroke-width="0.9" fill="none"/></svg>`;
}
function hammerGlyph(): string {
  return `<svg viewBox="0 0 16 16"><rect x="3" y="3" width="8" height="3.2" rx="0.8" fill="currentColor" transform="rotate(-30 7 4.6)"/><rect x="7" y="6" width="1.8" height="8" rx="0.6" fill="currentColor" transform="rotate(-30 8 10)"/></svg>`;
}
